import { ListItemContext } from 'types/events';

// contains all the MParticle attribute definitions (as defined in master data plan)
type AttributeDefinitions = {
  row_name: string;
  row_position: string;
  Series_Name: string;
  creative_position: string;
  Season_Name: string;
  Episode_Name: string;
  category: string;
  scroll_depth: string;
  Search_Term: string;
  Source_Site: string;
  Source_Page: string;
  Tile_Source: 'Tivo Screens' | 'CMS';
  tivo_query: string;
  login_type: string;
  LR_ID: string;
  channel_name: string;
  option_name:
    | 'Add Favorite Channel'
    | 'Remove Favorite Channel'
    | 'Go to Details Page';
};

export type VideoPayload = {
  Source_Page?: string;
  Air_Date?: string;
  Genre?: string;
  Closed_Caption?: string;
  Episode_Number?: string | null;
  Season_Number?: string | null;
  Episode_Name?: string;
  Series_Name?: string;
  Source_Site?: string;
  Content_ID?: string;
  listItemContext?: ListItemContext;
  Time_Elapsed?: string;
  Time_Remaining?: string;
  Length?: string;
  Content_Format?:
    | 'Series'
    | 'Movies'
    | 'Live Event'
    | 'Trailer'
    | 'EPG Channel'
    | '';
};

export type PageViewPayload = {
  Source_Page: string;
  login_status: boolean;
  Source_Site: string;
  Page_Title: string;
};

// used to define payload types to enforce uniform attributes
type Payload<T extends keyof AttributeDefinitions> = Pick<
  AttributeDefinitions,
  T
>;

export enum MParticleEvents {
  HERO_ENGAGEMENT = 'hero_engagement',
  ROW_ENGAGEMENT = 'row_engagement',
  SHOW_LIST_ITEM_CLICKED = 'show_page_engagement',
  SCROLL_TRACKING = 'scroll_tracking',
  HEADER_NAVIGATION = 'header_navigation',
  BROWSE_LIST_ITEM_CLICKED = 'browse_page_engagement',
  SEARCH_RESULT_CLICKED = 'search_result_clicked',
  FORM_SKIP = 'form_skip',
  PAGE_VIEW = 'page_view',
  LOGIN = 'login',
  LOGOUT = 'log_out',
  SIGN_UP_PASSWORD = 'sign_up_password',
  SIGN_UP_MAGIC_LINK = 'sign_up_magic_link',
  CTV_GET_STARTED = 'CTV_get_started',
  CHANNEL_ENGAGEMENT = 'channel_engagement',
  MY_STUFF_ADD = 'my_stuff_add',
  MY_STUFF_REMOVE = 'my_stuff_remove',
}

export enum MParticleVideoEvents {
  ERROR = 'Error',
  START = 'Video_Start',
  MIDPOINT = 'Video_50_Complete',
  FINISH = 'Video_Complete',
  RECOMMENDATION_AUTO_PLAY = 'Recommendation_Auto_Play',
  RECOMMENDATION_CLICKED = 'Recommendation_Clicked',
}

export enum MParticleInteractiveAdEvents {
  AD_SLOT_START = 'Ad_Slot_Start',
  AD_SLOT_COMPLETE = 'Ad_Slot_Complete',
  AD_EXIT = 'Ad_Exit',
}

export type HeaderNavigationPayload = Payload<'category'>;

export type PromotionCustomAttrs = Payload<
  'Series_Name' | 'creative_position' | 'Source_Page'
> &
  Partial<Payload<'Episode_Name'>>; // Only used for Live Events

export type ItemCustomAttrs = Payload<
  | 'creative_position'
  | 'row_name'
  | 'row_position'
  | 'Series_Name'
  | 'Tile_Source'
> &
  Partial<Payload<'Episode_Name'>>; // Only used for Live Events;

// note we are excluding showpage_header (see PR for EL-316)
export type ShowPageEngagement = Payload<
  | 'Series_Name'
  | 'Season_Name'
  | 'Episode_Name'
  | 'row_position' // use position in list season number falls
  | 'row_name' // use season number as value
  | 'creative_position'
  | 'Source_Page'
>;

export type BrowsePageEngagement = Payload<
  | 'row_name'
  | 'row_position'
  | 'creative_position'
  | 'Series_Name'
  | 'Source_Site'
  | 'Source_Page'
>;

export type SearchResultClicked = Payload<
  | 'row_name'
  | 'row_position'
  | 'creative_position'
  | 'Series_Name'
  | 'Search_Term'
  | 'Source_Site'
  | 'Source_Page'
>;

export type HeroEngagementPayload = Payload<
  | 'Episode_Name'
  | 'Source_Page'
  | 'tivo_query'
  | 'category'
  | 'Source_Site'
  | 'creative_position'
  | 'Series_Name'
>;

export type RowEngagementPayload = Payload<
  | 'Episode_Name'
  | 'tivo_query'
  | 'category'
  | 'Tile_Source'
  | 'Source_Page'
  | 'row_name'
  | 'row_position'
  | 'Source_Site'
  | 'creative_position'
  | 'Series_Name'
> &
  Partial<Payload<'Episode_Name'>>; // Only used for Live Events;

export type ScrollTrackingPayload = Payload<'scroll_depth' | 'Source_Page'>;

export type FormSkipPayload = Payload<'Source_Page' | 'Source_Site'> & {
  form_name: string;
};

export type LoginEventPayload = Payload<
  'login_type' | 'LR_ID' | 'Source_Site' | 'Source_Page'
>;

export type LogoutEventPayload = LoginEventPayload;

export type SignUpPasswordPayload = Payload<'Source_Site' | 'Source_Page'>;

export type SignUpMagicLinkPayload = Payload<'Source_Site' | 'Source_Page'>;

export type GetStartedPayload = Payload<'Source_Site' | 'Source_Page'>;

export type ChannelEngagementPayload = Payload<'channel_name' | 'option_name'>;

export type MyStuffAddRemovePayload = Payload<
  'Series_Name' | 'Source_Site' | 'Source_Page'
> & { row_name?: string };
